import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useQuery, useQueryClient } from 'react-query';

import {
  getGiftPartnerOverview,
  getGiftPartners,
  giftPartnerError,
  isGiftPartnerNotDeployed,
} from './giftPartner.controller';

export const useGiftPartnerOverview = () => {
  const { isAdmin, isGiftBrandSeller, userProfile } = useAuth();
  return useQuery(
    ['giftPartner', 'overview', LowerRole.GiftBrandSeller, userProfile?._id],
    async () => {
      try {
        return await getGiftPartnerOverview();
      } catch (e) {
        if (isGiftPartnerNotDeployed(e)) return null;
        throw new Error(giftPartnerError(e));
      }
    },
    { enabled: !isAdmin() && isGiftBrandSeller(), retry: false, staleTime: 30000 },
  );
};

export const useAdminGiftPartners = () => {
  const { isAdmin, userProfile } = useAuth();
  return useQuery(
    ['giftPartner', LowerRole.Admin, userProfile?._id],
    async () => {
      try {
        return await getGiftPartners(LowerRole.Admin);
      } catch (e) {
        if (isGiftPartnerNotDeployed(e)) return null;
        throw new Error(giftPartnerError(e));
      }
    },
    { enabled: isAdmin(), retry: false, staleTime: 30000 },
  );
};

/** Execute locally, without retaining secret-bearing responses in the mutation cache. */
export const useGiftPartnerAction = () => {
  const client = useQueryClient();
  const mounted = useRef(true);
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = async (task: () => Promise<void>, onSuccess?: () => void) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      await task();
      if (mounted.current) onSuccess?.();
    } catch (e) {
      if (mounted.current) setError(giftPartnerError(e));
    } finally {
      // Includes overview, both roles, and event histories, also on concurrent conflicts.
      await client.invalidateQueries(['giftPartner']);
      running.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return { run, busy, error };
};
